import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { filter, map, merge, of, take, tap } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { selectActiveUsers } from '@main-application/administration/store/selectors/administration.selectors';
import { IsVideoDirective } from '@shared/directives/is-video.directive';
import { EIcon } from '@shared/enums/icon.enum';
import { ImageOrientation } from '@shared/interfaces';
import { PipesModule } from '@shared/pipes/pipes.module';
import { AvatarModule } from '@ui-components/components/avatar/avatar.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import { InspectionStepModel } from '../../models/inspection-step.model';

@Component({
  selector: 'app-media-viewer',
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatIconModule, AvatarModule, IsVideoDirective, PipesModule],
})
export class MediaViewerComponent extends PuSubscribable {
  public data: {
    step: InspectionStepModel;
    imageUrls: {
      cloudUri: string;
      thumbnailFileUploadCloudUri: any;
      imageOrientation?: ImageOrientation;
      originalFileName?: string;
      internalName?: string;
      assigneeId?: number;
      created?: Date;
    }[];
    initialIndex: number;
    isEditorMode?: boolean;
    showMetaInfo?: boolean;
    isMobile?: boolean;
  } = inject(MAT_DIALOG_DATA);
  private dialogRef: MatDialogRef<MediaViewerComponent> = inject(MatDialogRef);
  private store: Store = inject(Store);

  EIcon = EIcon;
  currentIndex = 0;
  currentUrl: {
    cloudUri: string;
    thumbnailFileUploadCloudUri?: any;
    imageOrientation?: ImageOrientation;
    originalFileName?: string;
    internalName?: string;
    assigneeId?: number;
    created?: Date;
  };
  deletedImages: string[] = [];
  protected readonly ImageOrientation = ImageOrientation;
  private modalsService = inject(ModalsService);
  private cdr = inject(ChangeDetectorRef);

  users$ = this.store.select(selectActiveUsers);

  constructor() {
    super();
    this.currentUrl = this.data.imageUrls[this.data.initialIndex] || this.data.imageUrls[0];
    this.currentIndex = this.data.initialIndex || 0;
  }

  onChange(mediaUrl: string): void {
    this.currentUrl = this.data.imageUrls.find(el => el.cloudUri === mediaUrl);
    this.currentIndex = this.data.imageUrls.findIndex(el => el.cloudUri === mediaUrl);
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.goToPrevious();
    } else if (event.key === 'ArrowRight') {
      this.goToNext();
    } else if (event.key === ' ' || event.code === 'Space') {
      this.toggleVideoPlayback();
      event.preventDefault();
    }
  }

  close(): void {
    this.dialogRef.close({
      deletedImages: this.deletedImages,
    });
  }

  private confirmDeletionDialog() {
    const dialogRef = this.modalsService.openConfirmationModal({
      title: `Are you sure you want to remove this?`,
      confirmColor: 'warn',
      cancelLabel: 'Cancel',
      confirmLabel: 'Confirm',
    });

    const useCloseParentDialog = this.dialogRef.afterClosed().pipe(
      take(1),
      tap(() => dialogRef.close()),
      map(() => of(DialogResult.Fail))
    );

    const useCloseTargetDialog = dialogRef.afterClosed().pipe(take(1));

    return merge(useCloseParentDialog, useCloseTargetDialog).pipe(filter(result => result === DialogResult.Success));
  }

  deleteImage(cloudUri: string): void {
    this.confirmDeletionDialog()
      .subscribe(() => {
        const index = this.data.imageUrls.findIndex(el => el.cloudUri === cloudUri);
        if (index !== -1) {
          this.deletedImages.push(cloudUri);
          this.data.imageUrls.splice(index, 1);

          if (this.data.imageUrls?.length > 0) {
            this.currentUrl = this.data.imageUrls[0];
            this.currentIndex = 0;
          } else {
            this.close();
          }
        }
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  isFile(cloudUri: string): boolean {
    return cloudUri.endsWith('.pdf') || cloudUri.endsWith('.docx');
  }

  public isImage(url: string): boolean {
    if (!url) return;

    const extension = url?.split('.')?.pop()?.toLowerCase();
    return extension
      ? ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'mp4', 'avi', 'mov', 'mkv', 'wmv'].includes(extension)
      : false;
  }

  public openInNewTab(cloudUri: string): void {
    window.open(cloudUri, '_blank');
  }

  downloadFile(cloudUri: string): void {
    if (!cloudUri) return;

    fetch(cloudUri, { credentials: 'include' })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = cloudUri.split('/').pop() || 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(err => console.error('Download failed', err));
  }

  goToNext(): void {
    if (this.currentIndex === this.data.imageUrls.length - 1) {
      this.currentUrl = this.data.imageUrls[0];
      this.currentIndex = 0;
      this.scrollToCurrentThumb();
      this.cdr.markForCheck();
      return;
    }
    this.currentUrl = this.data.imageUrls[this.currentIndex + 1];
    this.currentIndex = this.currentIndex + 1;
    this.scrollToCurrentThumb();
    this.cdr.markForCheck();
  }

  goToPrevious(): void {
    if (this.currentIndex === 0) {
      this.currentUrl = this.data.imageUrls[this.data.imageUrls.length - 1];
      this.currentIndex = this.data.imageUrls.length - 1;
      this.scrollToCurrentThumb();
      this.cdr.markForCheck();
      return;
    }
    this.currentUrl = this.data.imageUrls[this.currentIndex - 1];
    this.currentIndex = this.currentIndex - 1;
    this.scrollToCurrentThumb();
    this.cdr.markForCheck();
  }

  private toggleVideoPlayback(): void {
    const videoElement = document.querySelector('video.image-size') as HTMLVideoElement;
    if (videoElement) {
      if (videoElement.paused) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    }
  }

  private scrollToCurrentThumb(): void {
    const currentId = `thumb-${this.currentUrl.cloudUri}`;
    const el = document.getElementById(currentId);
    if (el) {
      el.scrollIntoView();
    }
  }

  getExtension(url: string): string {
    const cleanUrl = url.split(/[?#]/)[0];
    const extension = cleanUrl.split('.').pop()?.toUpperCase();
    return extension ?? 'file';
  }
}
